// src/pages/tournament/RankingPanel.jsx
import React from 'react';
import RankingTable from '../../components/ranking/RankingTable';
import TeamRankingTable from '../../components/ranking/TeamRankingTable';
import { TrophyIcon } from '../../components/icons';
import { groupName } from '../../utils/groups';

export const hasResults = (standings) =>
    standings.some((row) => row.matchesWon + row.matchesLost + row.matchesDrawn > 0);

// What the ranking method does, from the description the backend gives (or the original score's, without one).
export function ScoreHelp({ ranking }) {
    return (
        <details className="score-help">
            <summary>How is the score calculated?</summary>
            {ranking ? (
                <>
                    <p>
                        <strong>{ranking.name}:</strong> {ranking.summary}
                    </p>
                    <ul>
                        {ranking.how.map((line) => (
                            <li key={line}>{line}</li>
                        ))}
                        <li>
                            If scores are equal, the sets won minus lost decide (for the classic score: the point
                            difference), then the point difference and the points scored.
                        </li>
                        <li>
                            <strong>Games, breaks and repeats</strong> are shown to see how evenly the rounds were shared and
                            how often players met the same opponents or teammates again.
                        </li>
                        <li>Inactive players stay in the ranking with everything they have played.</li>
                    </ul>
                    <p className="hint">The ranking method can be changed in the tournament's ranking and matchmaking settings.</p>
                    <div className="score-tradeoffs">
                        <div>
                            <strong>Good at</strong>
                            <ul>
                                {ranking.strengths.map((line) => (
                                    <li key={line}>{line}</li>
                                ))}
                            </ul>
                        </div>
                        <div>
                            <strong>Gives up</strong>
                            <ul>
                                {ranking.tradeoffs.map((line) => (
                                    <li key={line}>{line}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </>
            ) : (
                <ul>
                    <li>
                        <strong>Per match:</strong> for every set, your team gets two thirds of its share of the points, plus
                        one third for winning the set. That is averaged over the match's sets, and winning the match adds 1.
                    </li>
                    <li>
                        <strong>Score</strong> is the sum over all your matches. If scores are equal, the point difference
                        decides, then the points scored.
                    </li>
                    <li>
                        <strong>Games, breaks and repeats</strong> do not change the score. They show how evenly the rounds
                        were shared and how often players met the same opponents or teammates again.
                    </li>
                    <li>Inactive players stay in the ranking with everything they have played.</li>
                </ul>
            )}
        </details>
    );
}

function RankingPanel({ standings, ranking, loading, failed, onOpenFinal, byTeam = false, grouped = false }) {
    const Table = byTeam ? TeamRankingTable : RankingTable;
    return (
        <section aria-labelledby="ranking-heading">
            <div className="rounds-toolbar">
                <div>
                    <h2 id="ranking-heading">{byTeam ? 'Team ranking' : 'Ranking'}</h2>
                    <p className="hint">
                        Best first.{!byTeam && ' Arrows show the change since the previous round.'}
                    </p>
                </div>
                <button className="btn btn-primary" onClick={onOpenFinal} disabled={standings.length === 0}>
                    <TrophyIcon size={16} /> Final ranking
                </button>
            </div>

            {loading ? (
                <div className="skeleton" aria-busy="true" />
            ) : failed && standings.length === 0 ? (
                <div className="empty">
                    <strong>The ranking could not be loaded</strong>
                    <p>Please try again in a moment.</p>
                </div>
            ) : standings.length === 0 ? (
                <div className="empty">
                    <strong>No {byTeam ? 'teams' : 'players'} yet</strong>
                    <p>{byTeam ? 'Make teams and play a round to see the ranking.' : 'Add players and play a round to see the ranking.'}</p>
                </div>
            ) : (
                <div className="card ranking-card">
                    {!hasResults(standings) && (
                        <p className="hint ranking-note">
                            No scores entered yet, so {byTeam ? 'teams are' : 'players are'} listed in the order they were made.
                        </p>
                    )}
                    {grouped ? (
                        <>
                            {[...new Set(standings.map((row) => row.group))].sort((a, b) => a - b).map((group) => (
                                <div key={group} className="ranking-group">
                                    <h3>Group {groupName(group)}</h3>
                                    <TeamRankingTable
                                        standings={standings.filter((row) => row.group === group).sort((a, b) => a.groupRank - b.groupRank)}
                                        ranking={ranking || undefined}
                                        rankKey="groupRank"
                                    />
                                </div>
                            ))}
                            <p className="hint ranking-note">
                                Standings of the group games. The knockout results count towards the final placement
                                only, which the Final ranking shows.
                            </p>
                        </>
                    ) : (
                        <Table standings={standings} ranking={ranking || undefined} />
                    )}
                    <ScoreHelp ranking={ranking} />
                </div>
            )}
        </section>
    );
}

export default RankingPanel;
